// @flow
import styled, { css } from "styled-components";
import {
  defaultTextFont,
  fontBold,
  fontNormal,
  fontCondensed
} from "@fdr/static-ui/Typography";
import Icon from "@fdr/static-ui/Icons";
import buildColor from "@fdr/static-ui/ColorPalette";

const colorDefault = {
  textColor: { color: "grey", variation: "D4" },
  backgroundColor: { color: "white", variation: "100" }
};

const colorByType = {
  success: {
    textColor: { color: "white", variation: "100" },
    backgroundColor: { color: "green", variation: "500" }
  },
  softSuccess: {
    textColor: { color: "green", variation: "500" },
    backgroundColor: colorDefault.backgroundColor
  },
  error: {
    textColor: { color: "red", variation: "500" },
    backgroundColor: colorDefault.backgroundColor
  },
  navigation: {
    textColor: { color: "white", variation: "100" },
    backgroundColor: { color: "navy", variation: "900" }
  },
  whiteNavigation: {
    textColor: { color: "navy", variation: "700" },
    backgroundColor: colorDefault.backgroundColor
  },
  default: colorDefault,
  defaultBold: {
    textColor: { color: "white", variation: "100" },
    backgroundColor: { color: "blue", variation: "800" }
  },
  login: {
    textColor: { color: "navy", variation: "700" },
    backgroundColor: colorDefault.backgroundColor
  },
  dark: {
    textColor: { color: "white", variation: "100" },
    backgroundColor: { color: "navy", variation: "default" }
  }
};

export const TitleContainer = styled.div`
  display: flex;
  align-items: center;
  position: relative;
  padding-left: 16px;
  height: 54px;
  flex: 0 0 auto;
  color: ${buildColor(
    colorDefault.textColor.color,
    colorDefault.textColor.variation
  )};
  background-color: ${({ titleType }) =>
    colorByType[titleType]
      ? buildColor(
          colorByType[titleType].backgroundColor.color,
          colorByType[titleType].backgroundColor.variation
        )
      : buildColor(
          colorDefault.backgroundColor.color,
          colorDefault.backgroundColor.variation
        )};
  justify-content: ${(props) =>
    props.isNavigation ? "flex-start" : "space-between"};
  ${(props) =>
    !props.hasBack &&
    (props.hasSubtitle
      ? css`
          padding: 8px 16px;
        `
      : css`
          padding: 16px;
        `)}
  ${(props) =>
    props.roundedCorners &&
    css`
      border-radius: 4px 4px 0 0;
    `};
  ${(props) =>
    props.isSticky &&
    css`
      position: sticky;
      top: 0;
      z-index: 20;
    `};
  ${(props) =>
    props.hasShadow &&
    css`
      box-shadow: inset 0 -1px 0 ${props.titleType === "dark" ? buildColor(colorByType[props.titleType].backgroundColor.color, "D1") : buildColor("grey", "L2")};
      z-index: 2;
    `};
`;

export const NavigationTitleContainer = styled.div`
  & > button {
    border: 0;
    border-radius: 0;
    background: none;
    padding: 0;
    margin-right: 12px;
    ${({ hasPaddingLeft }) => hasPaddingLeft && `padding-left: 12px;`}

    &:hover {
      background: none;
    }
  }
`;

export const CloseButtonContainer = styled.div`
  ${({ titleType, titleAlign }) =>
    (titleType === "dark" || titleAlign === "center") &&
    css`
      position: absolute;
      right: 0;
    `}

  & > button {
    border: 0;
    border-radius: 0;
    background: none;
    flex: 0 0 40px;
    padding: 0 16px;

    &:hover {
      background: none;
    }
  }
`;

export const CloseLabel = styled.span`
  ${({ titleType }) =>
    titleType === "default"
      ? css`
          color: ${buildColor("blue", "D1")};
        `
      : css`
          font-size: 16px;
          font-family: ${fontBold};
          font-weight: 500;
          line-height: 125%;
        `};
`;

export const StatusIcon = styled(Icon)`
  margin-right: 8px;
`;

export const Title = styled.h1`
  font-size: 16px;
  font-family: ${fontBold};
  font-weight: 500;
  flex: 1 0 auto;
  height: 23px;
  line-height: 125%;
  letter-spacing: 0;
  display: flex;
  align-items: center;
  color: ${({ titleType }) =>
    colorByType[titleType]
      ? buildColor(
          colorByType[titleType].textColor.color,
          colorByType[titleType].textColor.variation
        )
      : buildColor(
          colorDefault.textColor.color,
          colorDefault.textColor.variation
        )};
  ${({ titleType, hasIcon, titleAlign }) => {
    if (titleType === "dark") {
      return css`
        justify-content: ${hasIcon ? "left" : "center"};
      `;
    }

    return css`
      justify-content: ${titleAlign};
    `;
  }}
  ${({ isCapitalized }) =>
    isCapitalized &&
    css`
      text-transform: capitalize;
    `};
`;

export const TitleHolder = styled.div`
  flex: 1 0 auto;
  display: inline-flex;
  flex-direction: column;
`;

export const WidgetContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-right: 8px;
`;

export const Subtitle = styled.strong`
  font-family: ${defaultTextFont};
  font-size: 12px;
  font-weight: normal;
  font-style: normal;
  height: 15px;
  line-height: 125%;
  text-transform: capitalize;
  color: ${buildColor(
    colorDefault.textColor.color,
    colorDefault.textColor.variation
  )};
  ${(props) =>
    props.useCloseLabel &&
    css`
      color: ${buildColor("grey", "D3")};
      letter-spacing: 1px;
      font-family: ${fontCondensed};
    `};
`;

export const NumberBadge = styled.span`
  background-color: ${buildColor("red", "300")};
  color: ${buildColor("white", "100")};
  border-radius: 50%;
  margin-left: 8px;
  width: 20px;
  height: 20px;
  font-size: 12px;
  font-family: ${fontNormal};
  font-weight: normal;
  display: flex;
  justify-content: center;
  align-items: center;
`;
